import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/ErrorBanner";
import { Loader } from "../components/Loader";
import { tg } from "../lib/telegram";
import { getRewards, redeemReward, type RewardItem, type RewardsPayload } from "../api/rewards";

export function Rewards() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<RewardsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  function load() {
    getRewards()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.show();
    const onBack = () => navigate(-1);
    app?.BackButton.onClick(onBack);
    return () => {
      app?.BackButton.offClick(onBack);
      app?.BackButton.hide();
    };
  }, [navigate]);

  async function redeem(item: RewardItem) {
    if (busy) return;
    if (!data || data.balance < item.cost_points) {
      tg()?.showAlert(t("rewards.notEnoughPoints"));
      return;
    }
    setBusy(item.id);
    try {
      const res = await redeemReward(item.id);
      tg()?.HapticFeedback?.impactOccurred("medium");
      setData((prev) => (prev ? { ...prev, balance: res.new_balance } : prev));
      tg()?.showAlert(t("rewards.premiumExtended"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      tg()?.showAlert(msg.includes("insufficient") ? t("rewards.notEnoughPoints") : t("rewards.errorPrefix", { msg }));
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorBanner message={t("rewards.errorLoad", { error })} />;
  if (!data) return <Loader text={t("common.loading")} full />;

  return (
    <div className="min-h-screen bg-surface-soft pb-12">
      <div
        className="relative px-5 pt-10 pb-8 text-center"
        style={{
          background:
            "radial-gradient(ellipse 100% 70% at 50% 0%, var(--color-brand-yellow-soft) 0%, transparent 70%), var(--color-surface)",
        }}
      >
        <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-500">
          {t("rewards.marketplaceLabel")}
        </div>
        <div className="mt-3 text-[40px] font-extrabold text-ink-900 leading-none tracking-tight">
          {data.balance}
        </div>
        <div className="text-[14px] text-ink-500 mt-1">{t("rewards.pointsOnAccount")}</div>
      </div>

      <div className="px-5 mt-6">
        <SectionLabel>{t("rewards.buyForPoints")}</SectionLabel>
        <div className="space-y-3">
          {data.items.map((it) => (
            <RewardCard
              key={it.id}
              item={it}
              balance={data.balance}
              busy={busy === it.id}
              onRedeem={() => void redeem(it)}
            />
          ))}
        </div>
      </div>

      <div className="px-5 mt-8 text-center">
        <p className="text-[13px] text-ink-500 leading-relaxed max-w-[320px] mx-auto">
          {t("rewards.partnersComingSoon")}
        </p>
      </div>
    </div>
  );
}

function RewardCard({
  item,
  balance,
  busy,
  onRedeem,
}: {
  item: RewardItem;
  balance: number;
  busy: boolean;
  onRedeem: () => void;
}) {
  const { t } = useTranslation();
  const canAfford = balance >= item.cost_points;
  return (
    <div className="bg-surface rounded-[var(--radius-lg)] p-4 border border-ink-100">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-bold text-ink-900">{item.title}</div>
          {item.description && (
            <div className="text-[13px] text-ink-500 mt-1 leading-relaxed">{item.description}</div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-[18px] font-extrabold leading-none ${canAfford ? "text-brand-yellow" : "text-ink-400"}`}>
            {item.cost_points}
          </div>
          <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mt-1">
            {t("points.plural", { count: item.cost_points })}
          </div>
        </div>
      </div>
      <Button
        stretched
        size="md"
        variant={canAfford ? "primary" : "secondary"}
        disabled={!canAfford || busy}
        onClick={onRedeem}
        className="mt-3"
      >
        {busy ? t("rewards.buying") : canAfford ? t("rewards.get") : t("rewards.insufficientPoints", { diff: item.cost_points - balance })}
      </Button>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-400 mb-3">
      {children}
    </div>
  );
}
